<script setup lang="ts">
/**
 * AIRA's chat drawer (Figma `Drawer` 440×970, "AIRA chat" flow): the playground's chat
 * column, on its own, on any page. Opened by the floating ball. One thread for the shopper:
 * what is said here is seen in the canvas column and back.
 *
 * The brief gathered by the stylist's `ask` lines travels in `answers` (contract §7b); her
 * commands run through the navigator (product / capsule / outfit / the canvas door); the
 * canvas-only ones have nothing to act on here.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Drawer from 'primevue/drawer'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import { airaProposeOutfitsOf, buildAiraEnvelope, type AiraCommand } from '@/features/aira/api'
import { useAiraDrawerStore, useAiraThreadStore } from '@/features/aira/model'
import { useAiraCommandNavigator } from '@/features/aira/contextual'
import { isStyleSpaceV2ApiEnabled } from '@/features/style-space/flags'
import {
  mapAiraThreadToPanelMessages,
  occasionLabelFromRequest,
  type FairStyleSpaceAiraChipAction,
} from '@/widgets/style-space/fair/fair-style-space-aira.data'
import { buildFairStyleSpaceChooseLocation } from '@/widgets/style-space/fair-v2/fair-style-space-v2-routes.data'
import FairStyleSpaceV2PlaygroundChat from '@/widgets/style-space/fair-v2/FairStyleSpaceV2PlaygroundChat.vue'
import '@/widgets/style-space/fair-v2/fair-style-space-v2-playground.css'

const { t, locale } = useI18n()
const drawer = useAiraDrawerStore()
const thread = useAiraThreadStore()
const tokenStore = useTokenStore()
const authModal = useAuthModal()
const { navigate } = useAiraCommandNavigator()
const router = useRouter()

const chatCommandsOn = isStyleSpaceV2ApiEnabled('playground_chat_commands')

const open = computed({
  get: () => drawer.open,
  set: (value: boolean) => (value ? drawer.show() : drawer.hide()),
})

/** The brief the `ask` lines gathered — sent back on every turn of the flow (§7b). */
const answers = ref<Record<string, string | string[]>>({})
const sentIds = new Set<string>()
const handled = new Set<string>()

watch(
  () => drawer.open,
  (isOpen) => {
    if (!isOpen) return
    if (!tokenStore.isAuthenticated()) {
      drawer.hide()
      authModal.showAuthModal('signInSignUpWrapper')
      return
    }
    void thread.ensureLoaded()
  },
)

// No canvas here, so the three proposals collapse into one card (entry 8).
const messages = computed(() =>
  mapAiraThreadToPanelMessages(thread.messages, {
    commands: chatCommandsOn,
    collapseProposals: true,
  }),
)
const hasConversation = computed(() => thread.messages.length > 0)
const sending = computed(() => thread.messages.some((message) => message.delivery === 'sending'))

function envelope() {
  return buildAiraEnvelope({ surface: 'main', answers: answers.value })
}

function send(text: string) {
  const id = thread.send({ text, envelope: envelope(), lang: locale.value })
  sentIds.add(id)
}

function runCommand(command: AiraCommand) {
  if (navigate(command)) drawer.hide()
}

function handleChip(action: FairStyleSpaceAiraChipAction) {
  switch (action.kind) {
    case 'say':
      send(action.text)
      break
    case 'answer':
      if (action.answerKey) answers.value = { ...answers.value, [action.answerKey]: action.value }
      send(action.text)
      break
    case 'answer-multi':
      if (action.answerKey) answers.value = { ...answers.value, [action.answerKey]: action.values }
      send(action.text)
      break
    case 'command':
      runCommand(action.command)
      break
    default:
      break
  }
}

/** The stylist's commands in her replies to THIS drawer's lines run once. */
watch(
  () => thread.messages,
  (list) => {
    if (!chatCommandsOn) return
    for (const message of list) {
      if (message.role !== 'aira' || !message.clientMessageId) continue
      if (!sentIds.has(message.clientMessageId) || handled.has(message.key)) continue
      if (message.status !== 'ok' || message.local) continue
      handled.add(message.key)
      for (const action of message.actions) {
        if (action.type === 'open_product' || action.type === 'style_product') runCommand(action)
      }
    }
  },
)

function handleOpenCapsule(capsuleId: number) {
  runCommand({ type: 'navigate', to: 'capsule', id: capsuleId })
}

/**
 * Entry 8: the card carries the shopper to the canvas already in the choose state. The three
 * outfits ride in history state — no second generation, and nothing is picked here.
 */
function handleOpenOutfits(messageId: string) {
  const message = thread.messages.find((entry) => entry.key === messageId)
  const proposal = message ? airaProposeOutfitsOf(message) : null
  if (!proposal?.outfits.length) return
  const occasion = occasionLabelFromRequest(proposal.request)
  void router.push(
    buildFairStyleSpaceChooseLocation({
      messageKey: messageId,
      occasion: occasion.code,
      occasionLabel: occasion.label,
      outfits: proposal.outfits,
    }),
  )
  drawer.hide()
}
</script>

<template>
  <Drawer
    v-model:visible="open"
    class="fair-ss2-pg__drawer fair-aira-drawer"
    position="right"
    style="width: min(440px, 100vw)"
    :header="t('fair_style_space.playground_v2.chat_label')"
  >
    <FairStyleSpaceV2PlaygroundChat
      state="empty"
      :messages="messages"
      :has-conversation="hasConversation"
      :selected-item="null"
      :sending="sending"
      @send="send"
      @chip="handleChip"
      @retry="thread.retry"
      @surprise="send(t('fair_style_space.playground_v2.surprise_me_line'))"
      @dress-today="send(t('fair_style_space.playground_v2.dress_me_today_line'))"
      @open-capsule="handleOpenCapsule"
      @open-outfits="handleOpenOutfits"
    />
  </Drawer>
</template>
